import { useEffect, useState } from "react";
import { SportProvider } from "../../models/interfaces/SportProvider"
import { Event } from "../../models/interfaces/Event";
import { Spinner, Nav, Tab } from "react-bootstrap";
import { FeaturedEventView } from "./featuredEvent/FeaturedEventView";
import { User } from "../../models/interfaces/User";

interface Props {
    sportProvider: SportProvider
    user ?: User
}

export function FeaturedEventsView({ sportProvider, user }: Props) {
    const [featuredEvents, setFeaturedEvents] = useState<Event[] | undefined>(
        undefined
    );
    const [eventsByCategory, setEventsByCategory] = useState<Map<string, Event[]>>(new Map());
    const [activeTab, setActiveTab] = useState<string | null>(null);

    useEffect(() => {
        sportProvider.fanResolverProviders.featuredEventProvider.once().then((resolvers) => {
            return Promise.all(
                resolvers.map((resolver) => {
                    return resolver.asAPromise();
                })
            ).then((events) => {
                setFeaturedEvents(events);
                
                // Group events by category
                const groupedEvents = new Map<string, Event[]>();
                events.forEach((event) => {
                    const category = event.attributes.category;
                    if (category) {
                        const categoryKey = category.name;
                        if (!groupedEvents.has(categoryKey)) {
                            groupedEvents.set(categoryKey, []);
                        }
                        groupedEvents.get(categoryKey)!.push(event);
                    }
                });
                
                setEventsByCategory(groupedEvents);
                
                // Set the first category as active tab
                if (groupedEvents.size > 0) {
                    setActiveTab(Array.from(groupedEvents.keys())[0]);
                }
            });
        });
    }, [sportProvider.fanResolverProviders]);

    const renderEventsForCategory = (events: Event[]) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {events.map((event) => {
                    return (
                        <div key={event.id}>
                            <FeaturedEventView sportProvider={sportProvider} event={event} user={user} />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        featuredEvents ?
            (featuredEvents.length > 0 ?
                (eventsByCategory.size > 1 ? (
                    <Tab.Container activeKey={activeTab || undefined} onSelect={(k) => setActiveTab(k)}>
                        <Nav variant="tabs" className="mb-3">
                            {Array.from(eventsByCategory.keys()).map((categoryName) => (
                                <Nav.Item key={categoryName}>
                                    <Nav.Link eventKey={categoryName}>
                                        {categoryName}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                        <Tab.Content>
                            {Array.from(eventsByCategory.entries()).map(([categoryName, events]) => (
                                <Tab.Pane key={categoryName} eventKey={categoryName}>
                                    {renderEventsForCategory(events)}
                                </Tab.Pane>
                            ))}
                        </Tab.Content>
                    </Tab.Container>
                ) : (
                    // If only one category or no categories, show events directly
                    renderEventsForCategory(featuredEvents)
                )) :
                <>No featured tournaments</>
            ) :
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Spinner animation="border" />
                <h4>Loading</h4>
            </div>
    )
}
